/**
 * Admin Settings Page (v5.0.0)
 *
 * 4-tab interface for managing application settings and diagnostic algorithm constants.
 * Each tab has independent save functionality and unsaved changes tracking.
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Admin/AdminLayout';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('application');
  const [groupedSettings, setGroupedSettings] = useState({});
  const [diagnosticSettings, setDiagnosticSettings] = useState({});
  const [diagnosticSettingsMeta, setDiagnosticSettingsMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [modifiedSettings, setModifiedSettings] = useState({});
  const [modifiedDiagnosticSettings, setModifiedDiagnosticSettings] = useState({});
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [exportingDatabase, setExportingDatabase] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showExportInstructions, setShowExportInstructions] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchDiagnosticSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/settings', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        setGroupedSettings(data.groupedSettings || {});
      } else {
        setError(data.error || 'Failed to load settings');
      }
    } catch (err) {
      console.error('Fetch settings error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnosticSettings = async () => {
    try {
      const response = await fetch('/api/diagnostic-settings', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        setDiagnosticSettings(data.settings || {});
        setDiagnosticSettingsMeta(data.settingsWithMeta || {});
      } else {
        console.error('Failed to load diagnostic settings:', data.error);
      }
    } catch (err) {
      console.error('Fetch diagnostic settings error:', err);
    }
  };


  const handleSettingChange = (key, value) => {
    setModifiedSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setSuccessMessage('');
    setError(null);
  };

  const handleDiagnosticSettingChange = (key, value) => {
    setModifiedDiagnosticSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setSuccessMessage('');
    setError(null);
  };


  const handleTabChange = (newTab) => {
    const hasUnsavedChanges =
      (activeTab === 'diagnostic') ? Object.keys(modifiedDiagnosticSettings).length > 0 :
      Object.keys(modifiedSettings).length > 0;

    if (hasUnsavedChanges) {
      setPendingTab(newTab);
      setShowUnsavedWarning(true);
    } else {
      setActiveTab(newTab);
      setError(null);
      setSuccessMessage('');
    }
  };

  const confirmTabChange = () => {
    // Discard unsaved changes and switch tab
    setModifiedSettings({});
    setModifiedDiagnosticSettings({});
    setActiveTab(pendingTab);
    setShowUnsavedWarning(false);
    setPendingTab(null);
    setError(null);
    setSuccessMessage('');
  };

  const cancelTabChange = () => {
    setShowUnsavedWarning(false);
    setPendingTab(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const settingsToUpdate = Object.entries(modifiedSettings).map(([key, value]) => ({
        key,
        value
      }));

      if (settingsToUpdate.length === 0) {
        setError('No settings have been modified');
        return;
      }

      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: settingsToUpdate })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(`Successfully updated ${data.count} setting(s)`);
        setModifiedSettings({});
        await fetchSettings();

        // Notify AdminNav to refresh app version if it was updated
        if (activeTab === 'application') {
          window.dispatchEvent(new Event('admin-settings-updated'));
        }
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDiagnosticSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const settingsToUpdate = Object.entries(modifiedDiagnosticSettings).map(([key, value]) => ({
        key,
        value
      }));

      if (settingsToUpdate.length === 0) {
        setError('No settings have been modified');
        return;
      }

      const response = await fetch('/api/diagnostic-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: settingsToUpdate })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(`Successfully updated ${data.count} diagnostic setting(s)`);
        setModifiedDiagnosticSettings({});
        await fetchDiagnosticSettings();
      } else {
        setError(data.error || 'Failed to save diagnostic settings');
      }
    } catch (err) {
      console.error('Save diagnostic settings error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (activeTab === 'diagnostic') {
      setModifiedDiagnosticSettings({});
    } else {
      setModifiedSettings({});
    }
    setSuccessMessage('');
    setError(null);
  };


  const handleExportDatabase = async () => {
    try {
      setExportingDatabase(true);
      setExportError(null);

      const response = await fetch('/api/admin/export-database', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'database-backup.sql';

      // Download file (same pattern as AdminReports CSV export)
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('Database exported successfully');

    } catch (err) {
      console.error('Export database error:', err);
      setExportError(err.message || 'Failed to export database');
    } finally {
      setExportingDatabase(false);
    }
  };

  const getSettingValue = (key, originalValue) => {
    return modifiedSettings.hasOwnProperty(key) ? modifiedSettings[key] : originalValue;
  };

  const getDiagnosticSettingValue = (key, originalValue) => {
    return modifiedDiagnosticSettings.hasOwnProperty(key) ? modifiedDiagnosticSettings[key] : originalValue;
  };

  const getTabFilteredSettings = (tab) => {
    const tabFilters = {
      application: ['maintenanceMode', 'applicationName', 'app_version'],
      email: [
        'emailNotificationsEnabled',
        'adminEmailCc',
        'emailInvitationSubject',
        'emailInvitationHtmlBody',
        'emailInvitationTextBody',
        'emailResultsSubject',
        'emailResultsHtmlBody',
        'emailResultsTextBody'
      ],
      text: ['footer_text']
    };

    const keys = tabFilters[tab] || [];
    const filtered = {};

    Object.entries(groupedSettings).forEach(([category, categorySettings]) => {
      categorySettings.forEach(setting => {
        if (keys.includes(setting.settingKey)) {
          if (!filtered[category]) {
            filtered[category] = [];
          }
          filtered[category].push(setting);
        }
      });
    });

    return filtered;
  };

  const getUnsavedCount = () => {
    if (activeTab === 'diagnostic') {
      return Object.keys(modifiedDiagnosticSettings).length;
    } else {
      return Object.keys(modifiedSettings).length;
    }
  };


  const renderSettingInput = (setting) => {
    const currentValue = getSettingValue(setting.settingKey, setting.settingValue);

    switch (setting.settingType) {
      case 'boolean':
        return (
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={currentValue === '1' || currentValue === true}
              onChange={(e) => handleSettingChange(setting.settingKey, e.target.checked ? '1' : '0')}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-daring-teal"></div>
            <span className="ms-3 text-sm font-medium text-gray-700">
              {currentValue === '1' || currentValue === true ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        );

      case 'email':
        return (
          <input
            type="email"
            value={currentValue || ''}
            onChange={(e) => handleSettingChange(setting.settingKey, e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
            placeholder="email@example.com"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={currentValue || ''}
            onChange={(e) => handleSettingChange(setting.settingKey, e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
          />
        );

      case 'longtext':
        return (
          <textarea
            value={currentValue || ''}
            onChange={(e) => handleSettingChange(setting.settingKey, e.target.value)}
            rows={setting.settingKey.includes('Html') ? 12 : 8}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal font-mono text-sm"
            placeholder={setting.settingKey.includes('Html') ? 'HTML email body...' : 'Plain text email body...'}
          />
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={currentValue || ''}
            onChange={(e) => handleSettingChange(setting.settingKey, e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
          />
        );
    }
  };

  const renderDiagnosticSettingInput = (key, value, dataType, description) => {
    const currentValue = getDiagnosticSettingValue(key, value);

    if (dataType === 'boolean') {
      return (
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={currentValue === 'true' || currentValue === true}
            onChange={(e) => handleDiagnosticSettingChange(key, e.target.checked ? 'true' : 'false')}
            className="sr-only peer"
          />
          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-daring-teal"></div>
          <span className="ms-3 text-sm font-medium text-gray-700">
            {currentValue === 'true' || currentValue === true ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      );
    } else if (dataType === 'number') {
      return (
        <div>
          <input
            type="number"
            step="0.001"
            value={currentValue || ''}
            onChange={(e) => handleDiagnosticSettingChange(key, e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal font-mono"
          />
          {description && (
            <p className="mt-1 text-xs text-gray-500">{description}</p>
          )}
        </div>
      );
    } else {
      return (
        <input
          type="text"
          value={currentValue || ''}
          onChange={(e) => handleDiagnosticSettingChange(key, e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
        />
      );
    }
  };

  const categoryLabels = {
    mckinsey_constants: 'McKinsey Research Constants',
    loss_attribution: 'Value Loss Attribution',
    act_research: 'ACT Coaching Research',
    persona_baselines: 'Persona Baselines'
  };

  const groupDiagnosticSettings = () => {
    const grouped = {};

    Object.entries(diagnosticSettings).forEach(([key, value]) => {
      // Infer category from key prefix
      let category = 'other';
      if (key.startsWith('mark_') || key.startsWith('sarah_')) {
        category = 'persona_baselines';
      } else if (key.includes('loss') || key.includes('weight')) {
        category = 'loss_attribution';
      } else if (key.includes('act_')) {
        category = 'act_research';
      } else {
        category = 'mckinsey_constants';
      }

      if (!grouped[category]) {
        grouped[category] = [];
      }

      // Get data type from metadata, default to 'number' for backward compatibility
      const meta = diagnosticSettingsMeta[key] || {};
      const dataType = meta.dataType || 'number';

      grouped[category].push({
        key,
        value,
        dataType,
        description: null
      });
    });

    return grouped;
  };

  if (loading) {
    return (
      <AdminLayout title="Settings">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-daring-teal mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'application', label: 'Application', icon: '⚙️' },
    { id: 'email', label: 'Email', icon: '✉️' },
    { id: 'text', label: 'Text', icon: '📝' },
    { id: 'diagnostic', label: 'Diagnostic Settings', icon: '🔬' }
  ];

  return (
    <AdminLayout title="Settings">
      <div className="space-y-6">
        {/* Unsaved Changes Warning Modal */}
        {showUnsavedWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Unsaved Changes</h3>
              <p className="text-gray-700 mb-6">
                You have unsaved changes. If you switch tabs now, your changes will be lost. Do you want to discard them?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelTabChange}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmTabChange}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {successMessage}
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" aria-label="Tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-daring-teal text-daring-teal bg-daring-teal bg-opacity-5'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="px-6 py-6 space-y-8">
            {activeTab === 'diagnostic' ? (
              // Diagnostic Settings Tab
              <>
                {Object.entries(groupDiagnosticSettings()).map(([category, settings]) => (
                  <div key={category}>
                    <h3 className="text-md font-medium text-gray-900 mb-4 border-b pb-2">
                      {categoryLabels[category] || category}
                    </h3>
                    <div className="space-y-6">
                      {settings.map(setting => (
                        <div key={setting.key} className="flex flex-col space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            {setting.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </label>
                          {renderDiagnosticSettingInput(setting.key, setting.value, setting.dataType, setting.description)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              // Application/Text Settings Tabs
              <>
                {Object.entries(getTabFilteredSettings(activeTab)).map(([category, categorySettings]) => (
                  <div key={category}>
                    <h3 className="text-md font-medium text-gray-900 mb-4 capitalize border-b pb-2">
                      {category} Settings
                    </h3>
                    <div className="space-y-6">
                      {categorySettings.map(setting => (
                        <div key={setting.settingKey} className="flex flex-col space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            {setting.settingKey.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          {setting.description && (
                            <p className="text-xs text-gray-500">{setting.description}</p>
                          )}
                          {renderSettingInput(setting)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Database Export Section - Only show in Application tab */}
            {activeTab === 'application' && (
              <div className="border-t pt-6 mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 border-b pb-2">
                  Database Backup
                </h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Export a complete SQL backup of the database for offline storage or analysis.
                    The export includes all tables, data, and schema definitions.
                  </p>

                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleExportDatabase}
                      disabled={exportingDatabase}
                      className="px-4 py-2 bg-daring-teal text-white rounded-md text-sm font-medium hover:bg-daring-navy disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                    >
                      {exportingDatabase ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Exporting...</span>
                        </>
                      ) : (
                        <>
                          <span>📥</span>
                          <span>Export Database</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setShowExportInstructions(true)}
                      className="text-sm text-daring-teal hover:text-daring-navy underline"
                    >
                      How to import this backup?
                    </button>
                  </div>

                  {exportError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                      {exportError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
            <button
              onClick={handleReset}
              disabled={getUnsavedCount() === 0 || saving}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset Changes
            </button>
            <button
              onClick={
                activeTab === 'diagnostic' ? handleSaveDiagnosticSettings :
                handleSave
              }
              disabled={getUnsavedCount() === 0 || saving}
              className="px-6 py-2 bg-daring-teal text-white rounded-md text-sm font-medium hover:bg-daring-navy disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Modified Settings Indicator */}
        {getUnsavedCount() > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            You have {getUnsavedCount()} unsaved change(s)
          </div>
        )}

        {/* Import Instructions Modal */}
        {showExportInstructions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  How to Import SQL Backup
                </h3>
                <button
                  onClick={() => setShowExportInstructions(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4 text-sm text-gray-700">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
                  <p className="text-xs">
                    <strong>New:</strong> Exports are now automatically reordered for compatibility with DB Browser.
                    No command line required!
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Using DB Browser for SQLite (Recommended):</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-2">
                    <li>Download and install <a href="https://sqlitebrowser.org/" target="_blank" rel="noopener noreferrer" className="text-daring-teal hover:underline">DB Browser for SQLite</a></li>
                    <li>Open DB Browser → File → New Database</li>
                    <li>Save as <code className="bg-gray-100 px-1 rounded">backup.db</code> → Cancel the "Create Table" dialog</li>
                    <li>File → Import → Database from SQL file</li>
                    <li>Select your downloaded .sql file → Open</li>
                    <li>Wait for import to complete (10-30 seconds)</li>
                    <li>All 24 tables should now be visible in the Database Structure tab</li>
                  </ol>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Alternative: SQLite Command Line (Advanced):</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`# macOS/Linux - sqlite3 is pre-installed
cd ~/Downloads
sqlite3 backup.db < daring-futures-backup-*.sql

# Then open backup.db in DB Browser to view data`}
                  </pre>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">What's included:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>All 24 database tables with complete schema</li>
                    <li>All data (leaders, diagnostics, results, settings)</li>
                    <li>All indexes and foreign key constraints</li>
                    <li>Export timestamp in filename</li>
                  </ul>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Exporting to Excel:</h4>
                  <p className="text-xs mb-2">
                    Once imported into DB Browser, you can export individual tables:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 ml-2 text-xs">
                    <li>Browse Data → Select table</li>
                    <li>File → Export → Table as CSV</li>
                    <li>Open CSV file in Excel</li>
                  </ol>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <p className="text-xs">
                    <strong>Note:</strong> This backup is read-only for offline analysis.
                    For complete documentation, see <code className="bg-white px-1 rounded">/docs/database-import-guide.md</code>
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowExportInstructions(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
